import { useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import styled from 'styled-components';

import {
    GetLocationsByWarehouseColumnIdQuery,
    LocationState,
    LocationType,
    LocationZone,
    useGetLocationsByWarehouseColumnIdQuery,
    useGetWarehouseColumnsQuery,
} from 'data/__generated__';

import { colors } from 'constants/colors';
import { PAGES } from 'constants/pages';

import { LocationActions } from 'pages/Locations/components/LocationActions';

import { CommonStatusTag } from 'components/CommonStatusTag';
import { TotemInput } from 'components/TotemInput';
import { ItemType } from 'components/TrelloView/Column';
import { TrelloView } from 'components/TrelloView/TrelloView';
import { LocationTrelloItem } from 'pages/Locations/components/LocationTrelloItem';
import { StatusTag } from 'pages/Locations/components/StatusTag';

import { Header, HeaderTitle } from 'components/Header';
import { Loader, LoaderModeType } from 'components/Loader';
import { PageTitle } from 'components/PageTitle';
import { WarehouseColumnSelect } from 'components/Select/WarehouseColumnSelect';

type SortedLocations = {
    dry: GetLocationsByWarehouseColumnIdQuery['locationsByWarehouseColumnId'];
    fresh: GetLocationsByWarehouseColumnIdQuery['locationsByWarehouseColumnId'];
    kumo: GetLocationsByWarehouseColumnIdQuery['locationsByWarehouseColumnId'];
    bakery: GetLocationsByWarehouseColumnIdQuery['locationsByWarehouseColumnId'];
    fruit: GetLocationsByWarehouseColumnIdQuery['locationsByWarehouseColumnId'];
    caterer: GetLocationsByWarehouseColumnIdQuery['locationsByWarehouseColumnId'];
};

export type LocationColumnType = {
    title: string | JSX.Element;
    items: (ItemType & {
        state: LocationState;
        locationType?: LocationType | null;
        productName?: string | null;
        datamatrix: string;
    })[];
};

export const LOCATION_STATES = [LocationState.Regular, LocationState.Hidden];

export const LOCATION_COLUMN_LABELS = {
    dry: 'Secs 🥜',
    fresh: 'Frais ❄️',
    kumo: 'Kumo 🍣',
    caterer: 'Traiteur 🍽️',
    fruit: 'Fruits 🍌',
    bakery: 'Boulangerie 🥐',
};

export const Locations = () => {
    const [searchParams, setSearchParams] = useSearchParams();
    const [hasProductIdEmpty, setHasProductIdEmpty] = useState<boolean>(false);
    const [selectedLocationIds, setSelectedLocationIds] = useState<string[]>([]);
    const [selectedWarehouseColumnId, setSelectedWarehouseColumnId] = useState<string | null>(null);
    const [sortedLocations, setSortedLocations] = useState<SortedLocations | null>(null);
    const filterString = searchParams.get('filterString') || '';
    const selectedStates = searchParams.getAll('state') as LocationState[];
    const selectedLocationTypes = searchParams.getAll('locationType') as LocationType[];

    const { data: warehouseColumnsData } = useGetWarehouseColumnsQuery();

    // Initialiser avec le premier entrepôt disponible
    useEffect(() => {
        if (!selectedWarehouseColumnId && warehouseColumnsData?.warehouseColumns?.length) {
            const firstWarehouseColumn = warehouseColumnsData.warehouseColumns[0];
            setSelectedWarehouseColumnId(firstWarehouseColumn._id);
        }
    }, [warehouseColumnsData, selectedWarehouseColumnId]);

    const warehouseColumnId = selectedWarehouseColumnId || '';

    const {
        data: locationsData,
        loading: locationsLoading,
        error: locationsError,
    } = useGetLocationsByWarehouseColumnIdQuery({
        variables: { warehouseColumnId },
        skip: !warehouseColumnId,
    });

    // Réinitialiser les locations sélectionnées quand on change d'entrepôt
    useEffect(() => {
        setSelectedLocationIds([]);
        setSortedLocations(null);
    }, [warehouseColumnId]);

    function toggleLocationSelection(locationIdToToggle: string) {
        if (selectedLocationIds.includes(locationIdToToggle)) {
            setSelectedLocationIds(selectedLocationIds.filter((locationId) => locationId !== locationIdToToggle));
        } else {
            setSelectedLocationIds([...selectedLocationIds, locationIdToToggle]);
        }
    }

    function getRegularLocationsToPrint() {
        if (!locationsData) return [];

        return locationsData.locationsByWarehouseColumnId
            .filter(
                (location) => selectedLocationIds.includes(location._id) && location.state === LocationState.Regular,
            )
            .map((location) => ({ datamatrix: location.datamatrix }));
    }

    function mapLocationsToTrelloItems(
        locations: GetLocationsByWarehouseColumnIdQuery['locationsByWarehouseColumnId'],
    ) {
        return locations.map((location) => {
            const { _id, state, locationType, datamatrix, product } = location;
            return {
                _id,
                content: (
                    <LocationTrelloItem
                        location={location}
                        isSelected={selectedLocationIds.includes(location._id)}
                        toggleSelection={() => toggleLocationSelection(location._id)}
                    />
                ),
                state,
                locationType,
                productName: product?.name,
                datamatrix,
            };
        });
    }

    useEffect(() => {
        if (locationsData) {
            const { locationsByWarehouseColumnId: locations } = locationsData;

            const sortedLocations = [...locations].sort((locationA, locationB) =>
                locationA.index > locationB.index ? 1 : -1,
            );

            setSortedLocations(
                sortedLocations.reduce<SortedLocations>(
                    (acc, location) => {
                        switch (location.zone) {
                            case LocationZone.Dry:
                                acc.dry.push(location);
                                break;
                            case LocationZone.Fre:
                                acc.fresh.push(location);
                                break;
                            case LocationZone.Kum:
                                acc.kumo.push(location);
                                break;
                            case LocationZone.Fru:
                                acc.fruit.push(location);
                                break;
                            case LocationZone.Bak:
                                acc.bakery.push(location);
                                break;
                            case LocationZone.Cat:
                                acc.caterer.push(location);
                                break;
                        }
                        return acc;
                    },
                    {
                        dry: [],
                        fresh: [],
                        kumo: [],
                        bakery: [],
                        fruit: [],
                        caterer: [],
                    },
                ),
            );
        }
    }, [locationsData]);

    if (locationsError) {
        throw new Error('Une erreur est survenue lors de la récupération des emplacements');
    }

    const trelloData = sortedLocations
        ? {
              columns: [
                  { title: LOCATION_COLUMN_LABELS.dry, items: mapLocationsToTrelloItems(sortedLocations.dry) },
                  { title: LOCATION_COLUMN_LABELS.fruit, items: mapLocationsToTrelloItems(sortedLocations.fruit) },
                  { title: LOCATION_COLUMN_LABELS.fresh, items: mapLocationsToTrelloItems(sortedLocations.fresh) },
                  { title: LOCATION_COLUMN_LABELS.kumo, items: mapLocationsToTrelloItems(sortedLocations.kumo) },
                  { title: LOCATION_COLUMN_LABELS.bakery, items: mapLocationsToTrelloItems(sortedLocations.bakery) },
                  { title: LOCATION_COLUMN_LABELS.caterer, items: mapLocationsToTrelloItems(sortedLocations.caterer) },
              ],
          }
        : null;

    function toggleStateSelection(state: LocationState) {
        return () => {
            if (selectedStates.includes(state)) {
                searchParams.delete('state', state);
                setSearchParams(searchParams);
            } else {
                searchParams.append('state', state);
                setSearchParams(searchParams);
            }
        };
    }

    function toggleLocationTypeSelection(locationType: LocationType) {
        return () => {
            if (selectedLocationTypes.includes(locationType)) {
                searchParams.delete('locationType', locationType);
                setSearchParams(searchParams);
            } else {
                searchParams.append('locationType', locationType);
                setSearchParams(searchParams);
            }
        };
    }

    function updateSearchParams(value: string): void {
        searchParams.set('filterString', value);
        setSearchParams(searchParams);
    }

    function filterColumns(dataColumns: LocationColumnType[]) {
        return dataColumns.map((column) => {
            const filteredStatesItems = column.items.filter(
                ({ state }) => !selectedStates.length || selectedStates.includes(state),
            );

            const filteredTypeItems = filteredStatesItems.filter(
                ({ locationType }) =>
                    !selectedLocationTypes.length || (locationType && selectedLocationTypes.includes(locationType)),
            );

            const filteredNameItems = filteredTypeItems.filter(
                ({ productName, datamatrix }) =>
                    !filterString ||
                    productName?.toLowerCase().includes(filterString.toLowerCase()) ||
                    datamatrix?.toLowerCase().includes(filterString.toLowerCase()),
            );

            const filteredItems = filteredNameItems.filter(({ productName }) => !hasProductIdEmpty || !productName);
            return { ...column, items: filteredItems };
        });
    }

    const hasActiveFilters =
        selectedStates.length > 0 || selectedLocationTypes.length > 0 || filterString.length > 2 || hasProductIdEmpty;

    return (
        <Container>
            <Header>
                <HeaderTitle>
                    <PageTitle page={PAGES.locations} />
                </HeaderTitle>
            </Header>
            <Content>
                <InnerContent>
                    <SearchSection>
                        <WarehouseColumnSelect
                            value={selectedWarehouseColumnId}
                            onChange={setSelectedWarehouseColumnId}
                            isClearable={false}
                        />
                        <TotemInput
                            label="Recherche"
                            onChange={(value) => updateSearchParams(value)}
                            placeholder="Chercher par datamatrix ou nom de produit (au moins 3 caractères)"
                            defaultValue={filterString}
                        />
                        <Filters>
                            <Title>Filtres</Title>
                            {LOCATION_STATES.map((state) => (
                                <FilterContainer
                                    key={state}
                                    $isSelected={selectedStates.includes(state)}
                                    onClick={toggleStateSelection(state)}
                                >
                                    <StatusTag state={state} />
                                </FilterContainer>
                            ))}
                            <FilterContainer
                                $isSelected={selectedLocationTypes.includes(LocationType.Picking)}
                                onClick={toggleLocationTypeSelection(LocationType.Picking)}
                            >
                                <CommonStatusTag
                                    backgroundColor={colors.orange}
                                    labelColor={colors.pureWhite}
                                    label="Picking"
                                />
                            </FilterContainer>
                            <FilterContainer
                                $isSelected={selectedLocationTypes.includes(LocationType.Reserve)}
                                onClick={toggleLocationTypeSelection(LocationType.Reserve)}
                            >
                                <CommonStatusTag
                                    backgroundColor={colors.blue}
                                    labelColor={colors.pureWhite}
                                    label="Réserve"
                                />
                            </FilterContainer>
                            <FilterContainer
                                $isSelected={hasProductIdEmpty}
                                onClick={() => setHasProductIdEmpty(!hasProductIdEmpty)}
                            >
                                <CommonStatusTag
                                    backgroundColor={colors.blue}
                                    labelColor={colors.pureWhite}
                                    label="Emplacement libre"
                                />
                            </FilterContainer>
                        </Filters>
                    </SearchSection>
                    <TrelloViewContainer>
                        {locationsLoading ? (
                            <Container>
                                <Loader mode={LoaderModeType.Spin} />
                            </Container>
                        ) : trelloData ? (
                            <TrelloView
                                initialData={
                                    hasActiveFilters ? { columns: filterColumns(trelloData.columns) } : trelloData
                                }
                                selectedItemIds={selectedLocationIds}
                                setSelectedItemIds={setSelectedLocationIds}
                                columnWidth="280px"
                            />
                        ) : null}
                    </TrelloViewContainer>
                </InnerContent>
                <LocationActions
                    selectedLocationIds={selectedLocationIds}
                    setSelectedLocationIds={setSelectedLocationIds}
                    regularLocationsToPrint={getRegularLocationsToPrint()}
                />
            </Content>
        </Container>
    );
};

const Container = styled.div`
    display: flex;
    flex-direction: column;
    overflow: hidden;
    flex: 1;
    height: 100%;
    background-color: ${({ theme }) => theme.backgroundColor};
`;

const Content = styled.div`
    position: relative;
    display: flex;
    flex: 1;
    overflow: hidden;
`;

const InnerContent = styled.div`
    display: flex;
    flex-direction: column;
    overflow: hidden;
`;

const SearchSection = styled.div`
    padding: 15px 15px 0;
`;
const TrelloViewContainer = styled.div`
    display: flex;
    flex: 1;
    overflow-x: scroll;
`;

const Filters = styled.div`
    display: flex;
    align-items: center;
    margin-top: 10px;

    & > :not(:first-child) {
        margin-left: 5px;
    }
`;

const Title = styled.h3`
    margin: 0;
    color: ${({ theme }) => theme.textColor};
    font-size: 20px;
    font-weight: 800;
`;

const FilterContainer = styled.div<{ $isSelected: boolean }>`
    cursor: pointer;
    border-radius: 20px;
    border: 2px solid ${({ $isSelected, theme }) => ($isSelected ? theme.darkBorderColor : 'transparent')};
`;
